import {Component} from '@taopix/taopix-core';
import {ROUTE_ASSISTANT} from '../../Constants';
import {BaseUserFlow, UserFlowList, UserFlowStep} from './BaseUserFlow';
import {UserFlowType} from './UserFlowFacade';
import {OptionsAndExtrasDisplayStage} from '../../Enums';

export class OrderUserFlow extends BaseUserFlow {
  protected readonly type = UserFlowType.Order;

  /**
   * @inheritdoc
   */
  public canChangeStep(step: string): boolean {
    return false;
  }

  /**
   * @inheritdoc
   */
  public configure(): UserFlowList {
    super.configure();
    const newSteps: UserFlowList = [];

    const upgradePrompt = this.getPromptUpgrade();
    if (null !== upgradePrompt) {
      newSteps.push(`${UserFlowStep.Upgrade}.${upgradePrompt}`);
    }

    const signIn = this.getSignIn();
    if (null !== signIn) {
      newSteps.push(signIn);
    }

    const getNameProject = this.getNameProject();
    if (null !== getNameProject) {
      newSteps.push(`${UserFlowType.Save}.${getNameProject}`);
    }
    newSteps.push(`${UserFlowType.Save}.${UserFlowStep.Save}`);

    newSteps.push(`${UserFlowStep.FlightCheck}.${UserFlowStep.FlightCheckInitialise}`);

    const optionsAndExtras = this.getOptionsAndExtras();
    if (null !== optionsAndExtras) {
      newSteps.push(`${UserFlowType.OptionsAndExtras}.${optionsAndExtras}`);
    }
    newSteps.push(`${this.type}.${UserFlowStep.Order}`);

    return newSteps;
  }

  /**
   * Returns if the sign in step needs to be shown.
   *
   * @returns The UserFlowStep.Login step, or null if the sign in dialog in can't be shown.
   */
  private getSignIn = (): UserFlowStep | string | null => {
    if (!this.designerSettings.authentication.canSignIn) return null;
    if (this.designerSettings.authentication.userRegistered) return null;
    return `${UserFlowStep.Auth}.${UserFlowStep.Login}`;
  };

  /**
   * Returns if the upgrade prompt needs to be shown.
   *
   * @returns The UserFlowStep.Upgrade step, or null if the sigin in can't be shown.
   */
  private getPromptUpgrade = (): UserFlowStep | null => {
    const {experience} = this;
    return `#/${ROUTE_ASSISTANT}/` !== this.designerSettings.project.stage &&
      experience.EDITOR.upgradeProject.showUpgradeProjectPromptOnOrder &&
      this.productLayout.upgradelayoutref > 0
      ? UserFlowStep.Upgrade
      : null;
  };

  /**
   * Returns if the project name dialog needs to be shown.
   *
   * @returns The UserFlowStep.ProjectName step, or null if the dialog can't be shown.
   */
  private getNameProject = (): UserFlowStep | null => {
    return this.uiState.projectNaming.editName ? UserFlowStep.ProjectName : null;
  };

  /**
   * Returns if the Options & Extras dialog needs to be shown.
   *
   * @returns The UserFlowStep.OptionsAndExtras step, or null if the dialog can't be shown.
   */
  private getOptionsAndExtras = (): UserFlowStep | null => {
    // Always show on order.
    // If no components + image enhancement off + !showOrderSummaryWindow - skip options and extras
    const {price} = this.designerSettings;

    const productComponents =
      price.pricing !== null &&
      price.pricing.components.hasOwnProperty('PRODUCT') &&
      this.experience.EDITOR.componentOptions.showComponentOptionsOnOrder
        ? Component.flattenComponentTree(
            Object.values((price.pricing.components.PRODUCT || {children: []}).children)
          ).filter(component => {
            return (
              ['TAOPIXAI', 'CALENDARCUSTOMISATION'].indexOf(component.sectioncode) === -1 &&
              ((component.displaystage as OptionsAndExtrasDisplayStage) &
                OptionsAndExtrasDisplayStage.Order) ===
                OptionsAndExtrasDisplayStage.Order
            );
          })
        : [];

    if (
      productComponents.length === 0 &&
      (!this.experience.SETTINGS.autoImageEnhancement.enhanceAllUserImagesAuto ||
        (this.experience.SETTINGS.autoImageEnhancement.enhanceAllUserImagesAuto &&
          !this.experience.SETTINGS.autoImageEnhancement.allowCustomersToOptOut)) &&
      !this.experience.SETTINGS.orderSummaryWindow.showOrderSummaryWindow
    ) {
      return null;
    }
    return UserFlowStep.OptionsAndExtras;
  };
}
